/**
 * FreeTextAnnot — a "type anywhere" note the nurse dropped on the page.
 * Coordinates are PDF points (bottom-left origin); rendered via `scale` (px per pt).
 */
import { useRef } from "react";
import { GripVertical, X } from "lucide-react";

export default function FreeTextAnnot({ annot, scale, pageHeightPts, autoFocus, onChange, onRemove }) {
  const dragRef = useRef(null);
  const left = annot.x * scale;
  const top = (pageHeightPts - annot.y - annot.height) * scale;
  const fontPx = (annot.font_size || 10) * scale;

  const startDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { sx: e.clientX, sy: e.clientY, x: annot.x, y: annot.y };
    const move = (ev) => {
      if (!dragRef.current) return;
      const dx = (ev.clientX - dragRef.current.sx) / scale;
      const dy = (ev.clientY - dragRef.current.sy) / scale;
      onChange({ ...annot, x: Math.max(0, dragRef.current.x + dx), y: dragRef.current.y - dy });
    };
    const stop = () => { dragRef.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", stop); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };

  return (
    <div
      className="absolute z-10 flex items-stretch group"
      style={{ left, top, width: annot.width * scale, minHeight: annot.height * scale }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      data-testid={`free-text-${annot.id}`}
    >
      <button
        type="button"
        onPointerDown={startDrag}
        className="shrink-0 w-3 bg-sky-500 text-white flex items-center justify-center cursor-move rounded-l-sm touch-none"
        title="Drag to move"
        data-testid={`free-text-move-${annot.id}`}
      >
        <GripVertical className="w-2.5 h-2.5" />
      </button>
      <textarea
        value={annot.text}
        autoFocus={autoFocus}
        rows={1}
        onChange={(e) => {
          const lines = Math.max(1, e.target.value.split("\n").length);
          onChange({ ...annot, text: e.target.value, height: Math.max(16, lines * (annot.font_size || 10) * 1.25 + 4) });
        }}
        placeholder="Type here…"
        className="flex-1 resize-none border-2 border-sky-400 bg-sky-50/80 text-slate-900 px-1 leading-tight outline-none focus:border-sky-600"
        style={{ fontSize: fontPx, height: annot.height * scale }}
        data-testid={`free-text-input-${annot.id}`}
      />
      <button
        type="button"
        onClick={onRemove}
        className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-rose-500 text-white items-center justify-center hidden group-hover:flex group-focus-within:flex"
        title="Remove note"
        data-testid={`free-text-remove-${annot.id}`}
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  );
}
